import { useState } from 'react';
import { LegacyLoginForm } from '../components/LegacyLoginForm';
import { ImportResultDisplay } from '../components/ImportResultDisplay';
import { ImportHistoryList } from '../components/ImportHistoryList';
import { RetryPasswordDialog } from '../components/RetryPasswordDialog';
import { useLegacyImport } from '../hooks/useLegacyImport';
import { useImportHistory } from '../hooks/useImportHistory';
import { DfeLegacyImportEntry } from '@/lib/api';
import { FavoriteButton } from '../../../components/FavoriteButton';

export function TransitionPage() {
  const { importResult, importing, error, performImport, retryImport, reset } = useLegacyImport();
  const { imports, loading: historyLoading, refresh: refreshHistory } = useImportHistory();
  const [retryTarget, setRetryTarget] = useState<DfeLegacyImportEntry | null>(null);

  const handleImport = async (server: string, username: string, password: string) => {
    await performImport(server, username, password);
    refreshHistory();
  };

  const handleReset = () => {
    reset();
  };

  const handleRetryFromResult = () => {
    if (!importResult) return;
    // Find the matching import entry from history, or build a synthetic one
    const entry = imports.find((i) => i.importId === importResult.importId);
    if (entry) {
      setRetryTarget(entry);
    } else {
      setRetryTarget({
        ...importResult,
        createdAt: new Date().toISOString(),
        updatedAt: null,
      });
    }
  };

  const handleRetryFromHistory = (entry: DfeLegacyImportEntry) => {
    setRetryTarget(entry);
  };

  const handleRetryConfirm = async (password: string) => {
    if (!retryTarget) return;
    setRetryTarget(null);
    await retryImport(retryTarget.importId, password);
    refreshHistory();
  };

  const handleRetryClose = () => {
    setRetryTarget(null);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-4">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">DFE Inventory Transition</h1>
          <FavoriteButton archeId="arche_dfe_inventory_transition" />
        </div>
        <p className="text-muted-foreground mt-1">
          Import your legacy DFE inventory into the Fias platform.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>
      )}

      {importResult ? (
        <ImportResultDisplay
          result={importResult}
          onReset={handleReset}
          onRetry={importResult.skippedItems.length > 0 ? handleRetryFromResult : undefined}
        />
      ) : (
        <LegacyLoginForm onSubmit={handleImport} disabled={importing} />
      )}

      <ImportHistoryList
        imports={imports}
        loading={historyLoading}
        onRetry={handleRetryFromHistory}
      />

      {retryTarget && (
        <RetryPasswordDialog
          open={!!retryTarget}
          onClose={handleRetryClose}
          onConfirm={handleRetryConfirm}
          disabled={importing}
          legacyUsername={retryTarget.legacyUsername}
          legacyServer={retryTarget.legacyServer}
          skippedCount={retryTarget.itemsSkipped}
        />
      )}
    </div>
  );
}
